import type {
  AvailabilityPrefs,
  ChatMessage,
  CoverRequest,
  DocumentItem,
  GroupThread,
  Goal,
  NotificationPrefs,
  ScheduleEvent,
  TaskItem,
  UpdatePost,
  User,
} from '../types'
import { localISODate } from '../lib/date'

export function threadIdFor(a: string, b: string) {
  return [a, b].sort().join('__')
}

export function isGroupThreadId(threadId: string) {
  return threadId.startsWith('grp-')
}

export const users: User[] = [
  { id: 'u-alex', name: 'Alex Turner', role: 'participant', color: '#6D5BD0', initials: 'AT' },
  { id: 'u-mark', name: 'Mark Turner', role: 'rep', title: 'Brother / Nominee', color: '#2fb6a8', initials: 'MT' },
  { id: 'u-jess', name: 'Jess Smith', role: 'support_worker', title: 'Support Worker', color: '#e08a3c', initials: 'JS' },
  { id: 'u-priya', name: 'Priya Nair', role: 'support_worker', title: 'Support Worker', color: '#d1558a', initials: 'PN' },
  { id: 'u-dan', name: 'Dan Okafor', role: 'support_worker', title: 'Support Worker', color: '#4f7cd4', initials: 'DO' },
  { id: 'u-amy', name: 'Amy Chen', role: 'allied_health', title: 'Occupational Therapist', color: '#3c78d8', initials: 'AC' },
  { id: 'u-sarah', name: 'Dr Sarah Lee', role: 'allied_health', title: 'Psychologist', color: '#7a5cd6', initials: 'SL' },
  { id: 'u-tom', name: 'Tom Williams', role: 'coordinator', title: 'Support Coordinator', color: '#3aa16d', initials: 'TW' },
]

export const currentUserIdKey = 'schub.currentUserId'

const todayISO = () => localISODate()
const addDays = (days: number) => {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return localISODate(d)
}

export const seedEvents: ScheduleEvent[] = [
  {
    id: 'ev-1',
    title: 'Support Shift',
    type: 'shift',
    date: todayISO(),
    startTime: '09:00',
    endTime: '13:00',
    assignees: [{ userId: 'u-jess', status: 'accepted' }],
    createdByUserId: 'u-alex',
    notes: 'Morning routine, grocery run, gym.',
  },
  {
    id: 'ev-movies',
    title: 'Movies — See Spiderman',
    type: 'appointment',
    date: todayISO(),
    startTime: '11:00',
    endTime: '13:30',
    assignees: [{ userId: 'u-alex', status: 'accepted' }],
    createdByUserId: 'u-alex',
    notes: 'Going to the movies to see Spiderman.',
  },
  {
    id: 'ev-2',
    title: 'Physio Appointment',
    type: 'appointment',
    date: todayISO(),
    startTime: '14:00',
    endTime: '15:00',
    assignees: [{ userId: 'u-alex', status: 'accepted' }],
    createdByUserId: 'u-mark',
    notes: 'Bring the exercise sheet from Amy.',
  },
  {
    id: 'ev-3',
    title: 'Team Meeting',
    type: 'meeting',
    date: todayISO(),
    startTime: '16:30',
    endTime: '17:00',
    assignees: [{ userId: 'u-alex', status: 'accepted' }],
    createdByUserId: 'u-tom',
  },
  {
    id: 'ev-4',
    title: 'Support Shift',
    type: 'shift',
    date: addDays(1),
    startTime: '09:00',
    endTime: '13:00',
    assignees: [{ userId: 'u-priya', status: 'pending' }],
    createdByUserId: 'u-alex',
  },
  {
    id: 'ev-5',
    title: 'OT Session',
    type: 'appointment',
    date: addDays(2),
    startTime: '11:00',
    endTime: '12:00',
    assignees: [{ userId: 'u-alex', status: 'accepted' }],
    createdByUserId: 'u-amy',
  },
]

export const seedTasks: TaskItem[] = [
  {
    id: 'task-1',
    title: 'Pick up prescription from chemist',
    description: 'Terry White Chemmart on Main St, ready after 2pm.',
    assignedToUserId: 'u-jess',
    assignedByUserId: 'u-alex',
    dueDate: todayISO(),
    priority: 'high',
    status: 'todo',
    createdAt: new Date().toISOString(),
  },
  {
    id: 'task-2',
    title: 'Practice new sensory routine with Alex',
    description: 'See notes from Amy in Documents > Therapy Recommendations.',
    assignedToUserId: 'u-jess',
    assignedByUserId: 'u-amy',
    dueDate: todayISO(),
    priority: 'medium',
    status: 'todo',
    createdAt: new Date().toISOString(),
  },
  {
    id: 'task-3',
    title: 'Book dentist check-up',
    assignedToUserId: 'u-priya',
    assignedByUserId: 'u-mark',
    dueDate: addDays(3),
    priority: 'low',
    status: 'todo',
    createdAt: new Date().toISOString(),
  },
  {
    id: 'task-4',
    title: 'Confirm transport for Friday OT session',
    assignedToUserId: 'u-jess',
    assignedByUserId: 'u-tom',
    dueDate: addDays(1),
    priority: 'medium',
    status: 'done',
    createdAt: new Date().toISOString(),
  },
]

export const seedUpdates: UpdatePost[] = [
  // A structured handover, written on the Shift Handover screen and published
  // into this one feed.
  {
    id: 'up-0',
    authorId: 'u-priya',
    type: 'handover',
    text: [
      'How the shift went: Settled afternoon at home after a busy morning.',
      'Mood & wellbeing: Calm, a little tired from the early start.',
      'Meals & drinks: Ate a full lunch, drank plenty of water.',
      'Things the next worker should know: A quiet start tomorrow would suit better.',
      'Follow-up needed: Physio asked for the new exercise sheet to be printed.',
    ].join('\n'),
    createdAt: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
    notifyBySms: false,
    documentationStyle: 'standard',
    linkedEventId: 'ev-1',
    handover: {
      summary: 'Settled afternoon at home after a busy morning.',
      mood: 'Calm, a little tired from the early start.',
      meals: 'Ate a full lunch, drank plenty of water.',
      medication: '',
      activities: 'Short walk to the shops, then music at home.',
      personalCare: '',
      communication: 'Preferred fewer questions when tired — short choices worked well.',
      nextShift: 'A quiet start tomorrow would suit better.',
      followUp: 'Physio asked for the new exercise sheet to be printed.',
      concerns: '',
    },
  },
  // An older free-text handover, kept exactly as it was written.
  {
    id: 'up-1',
    authorId: 'u-jess',
    type: 'handover',
    text: 'Alex had a great morning at the park. Mood good, ate full breakfast.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    notifyBySms: false,
  },
  {
    id: 'up-2',
    authorId: 'u-amy',
    type: 'instruction',
    text: 'Added new sensory recommendation — please check the updated Therapy Recommendations doc before the next shift.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 20).toISOString(),
    notifyBySms: true,
  },
  {
    id: 'up-3',
    authorId: 'u-mark',
    type: 'shopping',
    text: 'Weekly shop',
    checklist: [
      { id: 'c1', text: 'Milk (2L, lactose-free)', done: false },
      { id: 'c2', text: 'Bananas', done: false },
      { id: 'c3', text: 'Wholegrain bread', done: true },
      { id: 'c4', text: 'Chicken breast', done: false },
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 30).toISOString(),
    notifyBySms: false,
  },
  {
    id: 'up-4',
    authorId: 'u-priya',
    type: 'general',
    text: 'Heads up — the lift in the building will be out for maintenance Thursday morning.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 40).toISOString(),
    notifyBySms: false,
  },
]

export const seedDocuments: DocumentItem[] = [
  {
    id: 'doc-1',
    name: 'NDIS Plan 2026.pdf',
    folder: 'Plans',
    fileType: 'pdf',
    sizeLabel: '1.2 MB',
    uploadedByUserId: 'u-mark',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 40).toISOString(),
    access: 'private',
    allowedUserIds: [],
  },
  {
    id: 'doc-2',
    name: 'Therapy Recommendations - Aug.docx',
    folder: 'Therapy Recommendations',
    fileType: 'docx',
    sizeLabel: '340 KB',
    uploadedByUserId: 'u-amy',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 20).toISOString(),
    access: 'everyone',
    allowedUserIds: [],
  },
  {
    id: 'doc-3',
    name: 'Behaviour Support Plan.pdf',
    folder: 'Plans',
    fileType: 'pdf',
    sizeLabel: '890 KB',
    uploadedByUserId: 'u-sarah',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
    access: 'custom',
    allowedUserIds: ['u-jess', 'u-priya', 'u-tom'],
  },
  {
    id: 'doc-4',
    name: 'Medication Schedule.pdf',
    folder: 'Reports',
    fileType: 'pdf',
    sizeLabel: '210 KB',
    uploadedByUserId: 'u-mark',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    access: 'custom',
    allowedUserIds: ['u-jess', 'u-priya'],
  },
  {
    id: 'doc-5',
    name: 'Weekly Progress Notes.docx',
    folder: 'Reports',
    fileType: 'docx',
    sizeLabel: '150 KB',
    uploadedByUserId: 'u-jess',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    access: 'everyone',
    allowedUserIds: [],
  },
  {
    id: 'doc-6',
    name: 'Bank & Centrelink details.pdf',
    folder: 'Other Documents',
    fileType: 'pdf',
    sizeLabel: '95 KB',
    uploadedByUserId: 'u-mark',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 60).toISOString(),
    access: 'private',
    allowedUserIds: [],
  },
]

export const seedGoals: Goal[] = [
  {
    id: 'goal-1',
    title: 'Be more independent in daily tasks',
    category: 'Daily Living',
    progressPercent: 75,
    notes: 'Cooking breakfast independently 4/7 days this week.',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
  },
  {
    id: 'goal-2',
    title: 'Improve communication skills',
    category: 'Social',
    progressPercent: 60,
    notes: 'Practising scripted phone calls with Dr Lee.',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
  },
  {
    id: 'goal-3',
    title: 'Build confidence in community access',
    category: 'Community',
    progressPercent: 40,
    notes: 'Weekly bus trip with support worker, no incidents last 2 weeks.',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
  },
]

export const seedNotificationPrefs: NotificationPrefs = {
  smsEnabled: true,
  whatsappEnabled: false,
  phoneNumber: '+61 4XX XXX XXX',
}

const dowKeys: AvailabilityPrefs['availableDays'] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const dayKey = (offsetDays: number) => {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  return dowKeys[d.getDay()]
}

// Seeded so that whichever of the two support shifts is handed back, another
// support worker is already showing a green availability dot for that day.
export const seedAvailability: AvailabilityPrefs[] = [
  { userId: 'u-jess', availableDays: Array.from(new Set([dayKey(0), dayKey(1), 'mon', 'wed', 'fri'])), showOnCalendar: true },
  { userId: 'u-priya', availableDays: Array.from(new Set([dayKey(0), dayKey(1), 'tue', 'thu', 'sat'])), showOnCalendar: true },
  { userId: 'u-dan', availableDays: Array.from(new Set([dayKey(0), dayKey(1), dayKey(2), 'sat', 'sun'])), showOnCalendar: true },
]

/**
 * No shift starts out needing cover — the workflow begins when an assigned
 * worker says they can't do it.
 */
export const seedCoverRequests: CoverRequest[] = []

export const seedGroups: GroupThread[] = [
  {
    id: 'grp-care-team',
    name: 'Care Team',
    participantIds: ['u-alex', 'u-mark', 'u-jess', 'u-priya', 'u-dan', 'u-tom'],
    createdByUserId: 'u-mark',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
  },
]

export const seedMessages: ChatMessage[] = [
  {
    id: 'msg-1',
    threadId: threadIdFor('u-alex', 'u-jess'),
    senderId: 'u-alex',
    text: "Hey Jess, are we still good for the gym before the chemist run tomorrow?",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    readBy: ['u-alex', 'u-jess'],
  },
  {
    id: 'msg-2',
    threadId: threadIdFor('u-alex', 'u-jess'),
    senderId: 'u-jess',
    text: "Yep, all good! I'll be there at 9am like usual.",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4.5).toISOString(),
    readBy: ['u-alex', 'u-jess'],
  },
  {
    id: 'msg-3',
    threadId: threadIdFor('u-alex', 'u-mark'),
    senderId: 'u-mark',
    text: 'Reminder — physio is at 2pm today, I put the exercise sheet in Documents.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    readBy: ['u-mark'],
  },
  {
    id: 'msg-grp-1',
    threadId: 'grp-care-team',
    senderId: 'u-tom',
    text: 'Want to get everyone together to go over the new plan review. Does next week work for people?',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    readBy: ['u-tom', 'u-mark', 'u-jess'],
  },
  {
    id: 'msg-grp-2',
    threadId: 'grp-care-team',
    senderId: 'u-mark',
    text: 'Works for us — mornings are best if possible.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 20).toISOString(),
    readBy: ['u-tom', 'u-mark', 'u-jess'],
  },
]

